import { Check, Search } from 'lucide-react'

import type { DataGridColumnSchema, DataGridFilter } from '../lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { DropdownMenuCheckboxItem } from '@/ui/dropdown-menu'
import { Input } from '@/ui/input'

import { useGridContext } from './data-grid-context'
import {
  DataGridFilterBody,
  DataGridFilterHeader,
  DataGridFilterPanel,
  type DataGridFilterPresentation,
} from './data-grid-filter-panel'
import {
  DataGridFilterChip,
  keepKeysOutOfMenu,
  type DataGridFilterPresentationProps,
} from './data-grid-filter-shared'

export function DataGridCategoryFilter({
  column,
  presentation,
  onBack,
}: DataGridFilterPresentationProps) {
  const { grid } = useGridContext()
  const current = grid.criteria.filter?.find((filter) => filter.column === column.id)
  const facetLoad = grid.facetState[column.id]
  const facet = facetLoad?.facet ?? grid.facets[column.id]
  const selected = current?.operator === 'in' && Array.isArray(current.values)
    ? current.values
    : []
  const emptySelected = current?.operator === 'empty'
  const supportsValues = column.operators.includes('in')
  const items: { value: string; label: string; count?: number }[] = [
    ...(supportsValues ? facet?.values ?? [] : []).map((option) => ({
      value: option.value,
      label: option.label,
      count: option.count,
    })),
  ]
  for (const value of selected) {
    if (!items.some((item) => item.value === value)) {
      items.push({ value, label: column.labels?.[value] ?? value, count: undefined })
    }
  }
  const summary = emptySelected
    ? 'Empty'
    : selected.length === 1
      ? items.find((item) => item.value === selected[0])?.label ?? selected[0]
      : selected.length > 1 ? `${selected.length} selected` : undefined

  const content = (
    <DataGridCategoryFilterContent
      column={column}
      items={items}
      selected={selected}
      emptySelected={emptySelected}
      supportsValues={supportsValues}
      search={facetLoad?.search ?? ''}
      loading={facetLoad?.isLoading ?? false}
      error={facetLoad?.error?.message}
      hasMore={supportsValues && (facet?.hasMore ?? false)}
      emptyCount={facet?.emptyCount}
      onBack={onBack}
      presentation={presentation}
    />
  )

  if (presentation !== 'chip') return content
  return (
    <DataGridFilterChip
      column={column}
      summary={summary}
      onOpenChange={(open) => {
        if (open && supportsValues) void grid.loadFacetOptions(column.id)
      }}
    >
      {content}
    </DataGridFilterChip>
  )
}

function DataGridCategoryFilterContent({
  column,
  items,
  selected,
  emptySelected,
  supportsValues,
  search,
  loading,
  error,
  hasMore,
  emptyCount,
  onBack,
  presentation,
}: {
  column: DataGridColumnSchema
  items: readonly { value: string; label: string; count?: number }[]
  selected: readonly string[]
  emptySelected: boolean
  supportsValues: boolean
  search: string
  loading: boolean
  error?: string
  hasMore: boolean
  emptyCount?: number
  onBack?: () => void
  presentation: DataGridFilterPresentation
}) {
  const { grid } = useGridContext()
  const selectedSet = new Set(selected)

  function replaceFilter(next?: DataGridFilter) {
    grid.setFilters([
      ...(grid.criteria.filter ?? []).filter((filter) => filter.column !== column.id),
      ...(next ? [next] : []),
    ])
  }

  return (
    <DataGridFilterPanel presentation={presentation} className="w-64">
      <DataGridFilterHeader label={column.label} onBack={onBack} />
      <DataGridFilterBody className="gap-0 p-0">
        {supportsValues ? (
          <div className="border-border border-b p-1.5">
            <div className="relative">
              <Search
                aria-hidden
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2"
              />
              <Input
                aria-label={`Search ${column.label} options`}
                value={search}
                onChange={(event) => void grid.loadFacetOptions(column.id, event.target.value)}
                onKeyDown={keepKeysOutOfMenu}
                placeholder={`Find ${column.label.toLocaleLowerCase()}…`}
                className="bg-muted/60 h-8 border-0 pr-2 pl-7 text-xs shadow-none"
              />
            </div>
          </div>
        ) : null}
        <div className="max-h-64 overflow-y-auto p-1">
          {items.map((item) => {
            const checked = selectedSet.has(item.value)
            return (
              <CategoryOption
                key={item.value}
                presentation={presentation}
                checked={checked}
                label={item.label}
                count={item.count}
                onToggle={() => {
                  const next = checked
                    ? selected.filter((value) => value !== item.value)
                    : [...selected, item.value]
                  replaceFilter(next.length
                    ? { column: column.id, operator: 'in', values: next }
                    : undefined)
                }}
              />
            )
          })}
          {column.operators.includes('empty') ? (
            <CategoryOption
              presentation={presentation}
              checked={emptySelected}
              label="Empty values"
              count={emptyCount}
              onToggle={() => replaceFilter(emptySelected ? undefined : {
                column: column.id,
                operator: 'empty',
                values: true,
              })}
            />
          ) : null}
          {loading && items.length === 0 ? (
            <p className="text-muted-foreground px-2 py-5 text-center text-xs">
              Loading options…
            </p>
          ) : null}
          {!loading && supportsValues && items.length === 0 ? (
            <p className="text-muted-foreground px-2 py-5 text-center text-xs">
              No values match.
            </p>
          ) : null}
          {hasMore ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="mt-1 w-full"
              disabled={loading}
              onClick={() => void grid.loadFacetOptions(column.id, search, true)}
            >
              {loading ? 'Loading…' : 'Load more options'}
            </Button>
          ) : null}
          {error ? <p role="alert" className="text-destructive px-2 py-1 text-xs">{error}</p> : null}
        </div>
        {grid.criteria.filter?.some((filter) => filter.column === column.id) ? (
          <button
            type="button"
            onClick={() => replaceFilter()}
            className="border-border text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:bg-accent w-full border-t px-3 py-2 text-center text-xs font-medium outline-hidden"
          >
            Clear {column.label.toLocaleLowerCase()}
          </button>
        ) : null}
      </DataGridFilterBody>
    </DataGridFilterPanel>
  )
}

function CategoryOption({
  presentation,
  checked,
  label,
  count,
  onToggle,
}: {
  presentation: DataGridFilterPresentation
  checked: boolean
  label: string
  count?: number
  onToggle: () => void
}) {
  const accessibleLabel = count === undefined ? label : `${label} (${count})`
  if (presentation === 'menu') {
    return (
      <DropdownMenuCheckboxItem
        checked={checked}
        indicator="checkbox"
        closeOnClick={false}
        aria-label={accessibleLabel}
        onCheckedChange={onToggle}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {count !== undefined ? (
          <span className="text-muted-foreground tabular-nums">{count}</span>
        ) : null}
      </DropdownMenuCheckboxItem>
    )
  }

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={accessibleLabel}
      onClick={onToggle}
      className="text-foreground hover:bg-accent focus-visible:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs outline-hidden"
    >
      <span className={cn(
        'border-input bg-background flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border',
        checked && 'border-primary bg-primary text-primary-foreground',
      )}>
        {checked ? <Check aria-hidden className="size-2.5" /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined ? (
        <span className="text-muted-foreground tabular-nums">{count}</span>
      ) : null}
    </button>
  )
}
